import { Mobile, MessageText, Sms } from "iconsax-reactjs";
import { Avatar, AvatarFallback, Badge } from "../../../shared/ui";
import { DataTableColumn } from "../../../shared/ui/DataTable";
import { timeAgo } from "../../../shared/lib/utils";
import { CHANNEL_OPTIONS, formatAudienceSummary } from "../constants/notificationData";
import { BroadcastNotification } from "../types";

const channelIcons = {
  email: Sms,
  push: Mobile,
  sms: MessageText,
};

export const getChannelLabel = (channel: BroadcastNotification["channel"]) =>
  CHANNEL_OPTIONS.find((option) => option.value === channel)?.label ?? channel;

export const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
};

export const formatTimestamp = (value: string) => {
  const date = new Date(value);
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return `${datePart} - ${timePart}`;
};

export const STATUS_LABELS: Record<BroadcastNotification["status"], string> = {
  draft: "Draft",
  "in-progress": "Sending...",
  sent: "Sent",
  failed: "Failed",
};

export const STATUS_CLASSNAMES: Record<BroadcastNotification["status"], string> = {
  draft: "bg-[#F7F8FD] text-[#6F739B]",
  "in-progress": "bg-[#FFF6E5] text-[#B78103]",
  sent: "bg-[#E7F8EC] text-[#11A811]",
  failed: "bg-[#FDEAEA] text-[#F73C3C]",
};

export const notificationColumns: Array<DataTableColumn<BroadcastNotification>> = [
  {
    id: "subject",
    header: "Broadcast",
    cellClassName: "min-w-[200px]",
    cell: (item) => (
      <div className="max-w-[280px]">
        <p className="truncate aku-body-small-medium text-[#161827]">
          {item.subject}
        </p>
        <p className="truncate text-[13px] text-[#6F739B]">{item.message}</p>
      </div>
    ),
  },
  {
    id: "channel",
    header: "Channel",
    cellClassName: "min-w-[110px] aku-body-small-regular",
    cell: (item) => {
      const ChannelIcon = channelIcons[item.channel];
      return (
        <div className="flex items-center gap-2 text-[#383B56]">
          <ChannelIcon size={18} className="text-[#6F739B]" />
          <span className="truncate">{getChannelLabel(item.channel)}</span>
        </div>
      );
    },
  },
  {
    id: "audience",
    header: "Audience",
    cellClassName: "min-w-[150px] aku-body-small-regular text-[#383B56]",
    cell: (item) => formatAudienceSummary(item.audience),
  },
  {
    id: "recipients",
    header: "Recipients",
    cellClassName: "min-w-[90px] aku-body-small-regular text-[#383B56]",
    cell: (item) => item.recipientCount.toLocaleString(),
  },
  {
    id: "sentBy",
    header: "Sent by",
    cellClassName: "min-w-[140px]",
    cell: (item) => (
      <div className="flex items-center gap-3">
        <Avatar className="size-8 bg-[#EEF1FB]">
          <AvatarFallback className="bg-[#EEF1FB] text-[12px] font-[500] text-[#3B4060]">
            {getInitials(item.sentBy)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate aku-body-small-medium">{item.sentBy}</span>
      </div>
    ),
  },
  {
    id: "status",
    header: "Status",
    cellClassName: "min-w-[100px]",
    cell: (item) => (
      <Badge variant="soft" className={STATUS_CLASSNAMES[item.status]}>
        {STATUS_LABELS[item.status]}
      </Badge>
    ),
  },
  {
    id: "sentAt",
    header: "Sent",
    cellClassName:
      "min-w-[140px] whitespace-nowrap aku-body-small-regular text-[#161827]",
    cell: (item) => (
      <div>
        <p>{timeAgo(item.sentAt)}</p>
        <p className="text-[12px] text-[#6F739B]">
          {formatTimestamp(item.sentAt)}
        </p>
      </div>
    ),
  },
];
